import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useLoginRedirect, useSetSession } from '@/hooks/useAuth';
import type { AuthUser } from '@monitoring-panel/types';

const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL || 'https://auth.advia.tech';
const ALLOWED_EMAIL_DOMAINS = ['@advia.tech'];
const ALLOWED_EMAILS = ['advia@tscfo.com'];

const isEmailAllowed = (email: string) =>
  ALLOWED_EMAILS.includes(email) || ALLOWED_EMAIL_DOMAINS.some(d => email.endsWith(d));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuth();
  const loginRedirect = useLoginRedirect();
  const setSession = useSetSession();
  const [searchParams] = useSearchParams();
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [unauthorizedEmail, setUnauthorizedEmail] = useState<string | null>(null);
  const processedRef = useRef(false);

  const tokenFromUrl = searchParams.get('token');

  useEffect(() => {
    if (tokenFromUrl && !processedRef.current) {
      processedRef.current = true;
      setIsProcessingToken(true);

      async function validateAndStoreToken() {
        try {
          const response = await fetch(`${AUTH_SERVICE_URL}/session`, {
            headers: {
              Authorization: `Bearer ${tokenFromUrl}`,
            },
          });

          if (!response.ok) {
            throw new Error('Invalid token');
          }

          const data = await response.json();
          const userEmail = data.user?.email as string;

          // Check if email is from allowed domain
          if (!userEmail || !isEmailAllowed(userEmail)) {
            setUnauthorizedEmail(userEmail);
            setIsProcessingToken(false);
            // Remove token from URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('token');
            window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
            return;
          }

          setSession({
            token: tokenFromUrl!,
            user: data.user as AuthUser,
            expiresAt: data.session?.expiresAt,
          });

          setAuthSuccess(true);
          setIsProcessingToken(false);

          // Remove token from URL without triggering re-render issues
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('token');
          window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
        } catch (err) {
          console.error('Auth validation error:', err);
          processedRef.current = false;
          setIsProcessingToken(false);
          loginRedirect();
        }
      }

      validateAndStoreToken();
    }
  }, [tokenFromUrl, setSession, loginRedirect]);

  // Check if already authenticated user has valid email domain
  useEffect(() => {
    if (isAuthenticated && user && !isEmailAllowed(user.email)) {
      setUnauthorizedEmail(user.email);
      setSession(null);
    }
  }, [isAuthenticated, user, setSession]);

  useEffect(() => {
    if (!isAuthenticated && !authSuccess && !tokenFromUrl && !isProcessingToken && !unauthorizedEmail) {
      loginRedirect();
    }
  }, [isAuthenticated, authSuccess, tokenFromUrl, isProcessingToken, unauthorizedEmail, loginRedirect]);

  if (unauthorizedEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-adv-sunken">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-adv-warning-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-adv-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="adv-page-title mb-2">Access Denied</h1>
          <p className="adv-section-desc mb-4">
            Only authorized email addresses are allowed.
          </p>
          <p className="text-adv-body-s text-adv-secondary mb-6">
            You tried to sign in with: <span className="font-bold text-adv-primary">{unauthorizedEmail}</span>
          </p>
          <button
            onClick={() => {
              setUnauthorizedEmail(null);
              processedRef.current = false;
              loginRedirect();
            }}
            className="adv-btn adv-btn--primary"
          >
            Try with different account
          </button>
        </div>
      </div>
    );
  }

  if (isProcessingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-adv-sunken">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-adv-gold mx-auto mb-4"></div>
          <p className="adv-section-desc">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !authSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-adv-sunken">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-adv-gold mx-auto mb-4"></div>
          <p className="adv-section-desc">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
